/** An error answer from the CI portal API. */
export class ApiError extends Error {
  constructor(
    /** HTTP status */
    readonly status: number,
    message: string,
    /** Validation messages, when the API sent some */
    readonly details?: string[],
  ) {
    super(message);
  }
}

type ApiRequestProps = {
  /** HTTP method; defaults to GET */
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  /** Path under the site, e.g. /api/mocks */
  path: string;
  /** JSON body */
  body?: unknown;
  /** Query parameters; undefined values are left out */
  query?: Record<string, string | number | boolean | undefined>;
};

/** Calls the portal's own API (not Better Auth's) and parses the JSON answer. */
export const apiRequest = async <T>({
  method = "GET",
  path,
  body,
  query,
}: ApiRequestProps): Promise<T> => {
  const url = new URL(path, window.location.origin);
  for (const [name, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== "") {
      url.searchParams.set(name, String(value));
    }
  }
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) {
    // No body; callers of DELETE ask for void.
    return undefined as T;
  }
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data ?? {}) as { error?: string; details?: unknown };
    throw new ApiError(
      response.status,
      error.error ?? response.statusText,
      Array.isArray(error.details) ? error.details.map(String) : undefined,
    );
  }
  return data as T;
};
